import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  ThemeProvider,
  Typography
} from '@mui/material';
import { StylesProvider } from '@mui/styles';
import { getSiteTheme } from '../theme/siteTheme';
import { useDocumentThemeSync } from '../theme/useDocumentThemeSync';

const buildSectionsFromContent = (mountNode) => {
  const contentRoot = mountNode.closest('.content');
  if (!contentRoot) return [];

  const sourceNodes = Array.from(contentRoot.children).filter((node) => !node.contains(mountNode));
  const sections = [];
  let currentSection = null;

  sourceNodes.forEach((node) => {
    const tag = node.tagName ? node.tagName.toUpperCase() : '';
    const heading = tag === 'H1' || tag === 'H2';

    if (heading) {
      if (currentSection) {
        sections.push(currentSection);
      }

      currentSection = {
        id: `about-section-${sections.length + 1}`,
        title: node.textContent?.trim() || 'Section',
        nodes: [],
      };
      return;
    }

    if (!currentSection) {
      currentSection = {
        id: 'about-section-1',
        title: 'Overview',
        nodes: [],
      };
    }

    currentSection.nodes.push(node);
  });

  if (currentSection) {
    sections.push(currentSection);
  }

  sections.forEach((section) => {
    section.html = section.nodes.map((node) => node.outerHTML).join('\n');
  });

  return sections.filter((section) => section.html && section.html.trim() !== '');
};

const AboutCards = () => {
  const [sections, setSections] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const isDarkMode = useDocumentThemeSync();
  const theme = useMemo(() => getSiteTheme(isDarkMode), [isDarkMode]);

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  useEffect(() => {
    const mountNode = document.getElementById('about-cards-root');
    if (!mountNode) return;

    const parsedSections = buildSectionsFromContent(mountNode);
    if (parsedSections.length === 0) return;

    const contentRoot = mountNode.closest('.content');
    if (contentRoot) {
      Array.from(contentRoot.children).forEach((node) => {
        if (!node.contains(mountNode)) {
          node.style.display = 'none';
        }
      });
    }

    setSections(parsedSections);
    setCollapsedSections(
      parsedSections.reduce((acc, section) => {
        acc[section.id] = false;
        return acc;
      }, {})
    );
  }, []);

  if (sections.length === 0) {
    return null;
  }

  return (
    <StylesProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            py: { xs: 1, md: 2 },
            px: { xs: 0, sm: 1, md: 2, lg: 3 },
          }}
        >
          <Grid container spacing={{ xs: 2.5, md: 3.5, lg: 4 }}>
            {sections.map((section) => {
              const isCollapsed = collapsedSections[section.id] ?? false;
              const bodyId = `${section.id}-body`;

              return (
              <Grid item xs={12} key={section.id}>
                <Card
                  id={section.id}
                  data-collapsible-card="about"
                  data-collapsed={String(isCollapsed)}
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: isDarkMode
                      ? '0 8px 20px rgba(0, 0, 0, 0.35)'
                      : '0 8px 24px rgba(47, 46, 42, 0.12)',
                    borderColor: isDarkMode
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(47,46,42,0.08)',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDarkMode
                        ? '0 12px 28px rgba(0, 0, 0, 0.45)'
                        : '0 12px 32px rgba(47, 46, 42, 0.16)',
                    },
                    backgroundImage: 'none',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3.25, lg: 4 } }}>
                    <Box
                      data-card-header="about"
                      sx={{
                        mb: isCollapsed ? 0 : 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        component="h2"
                        sx={{
                          mb: 0,
                          fontSize: { xs: '1.4rem', md: '1.6rem', lg: '1.75rem' },
                          lineHeight: 1.25,
                          fontWeight: 600
                        }}
                      >
                        {section.title}
                      </Typography>
                      <IconButton
                        type="button"
                        aria-label={isCollapsed ? `Expand ${section.title}` : `Collapse ${section.title}`}
                        aria-expanded={!isCollapsed}
                        aria-controls={bodyId}
                        onClick={() => toggleSection(section.id)}
                        data-card-affordance="collapse"
                        size="small"
                        sx={{
                          flexShrink: 0,
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 0,
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '0.9rem', lineHeight: 1, fontWeight: 700 }}>
                          {isCollapsed ? '+' : '−'}
                        </Box>
                      </IconButton>
                    </Box>
                    <Collapse in={!isCollapsed} timeout={220} unmountOnExit>
                      <Box
                        id={bodyId}
                        data-card-body="about"
                        sx={{
                          fontSize: { xs: '1rem', md: '1.06rem', lg: '1.12rem' },
                          lineHeight: { xs: 1.7, md: 1.8 },
                          '& p': { mb: { xs: 1.5, md: 1.75 } },
                          '& p:last-of-type': { mb: 0 },
                          '& ul, & ol': { pl: { xs: 2.5, md: 3 }, mb: { xs: 1.5, md: 2 } },
                          '& li': { mb: 0.8 },
                          '& strong': { fontWeight: 600 },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 1,
                            mt: 1.25,
                            mb: 1.25,
                          },
                          '& a': (theme) => theme.mixins.linkProse,
                        }}
                        dangerouslySetInnerHTML={{ __html: section.html }}
                      />
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            );
            })}
          </Grid>
        </Container>
      </ThemeProvider>
    </StylesProvider>
  );
};

export default AboutCards;
